import React, { Fragment, useState } from 'react'
import { RotateCcw as RotateCcwIcon } from 'react-feather'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { reverse } from 'named-urls'
import { isEmpty, size } from 'lodash'
import { Box, IconButton, Paper, Typography } from '@material-ui/core'

import { BaseDialog } from 'components'
import { Table, TableBody, TableCell, TableRow } from 'components/Table'
import { TrailsCard } from '..'

import changeCase from 'helpers/changeCase'
import formatters from 'helpers/formatters'
import useSnackbar from 'hooks/useSnackbar'
import helpers from 'helpers'

import useFetch from 'hooks/useFetch'

import theme from 'theme'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const TrainningJourneyTrail = ({ step = 1, disabled, user }) => {
  const [openDialog, setOpenDialog] = useState(false)
  const [loading, setLoading] = useState(false)

  const classes = useStyles()

  const { TRANSLATE_TYPES } = constants.trailsJourney
  const {
    JOURNEYS,
    CONCLUDED_STATUS,
    DOING_STATUS,
    NOT_INITIATED_STATUS,
    TRANSLATE_STATUS,
  } = constants.companyTrail

  const { imageToPdf } = helpers.file

  const history = useHistory()
  const snackbar = useSnackbar()
  const { response, isLoading } = useFetch(
    service.dponet.trainningJourneys.list,
    {},
    [],
  )

  const trainningJourneys = response?.data?.trainningJourneys || []

  const trails = trainningJourneys.map((journey) => {
    const isConcluded = journey?.status === CONCLUDED_STATUS
    const camelizedStatus = changeCase.methods.camelCase(journey?.status)

    return {
      title: TRANSLATE_TYPES[journey.certificationKind],
      subtitle: journey?.concludedAt
        ? formatters.ptBrFormatDateTime(journey?.concludedAt)
        : TRANSLATE_STATUS[camelizedStatus],
      concluded: true,
      status: camelizedStatus,
      action: {
        text: isConcluded ? 'Baixar' : 'Assistir',
        method: () =>
          handleTrailAction({
            journeyId: journey.id,
            isConcluded,
            certificationKid: journey.certificationKind,
          }),
      },
    }
  })

  const concludedJourneys = trainningJourneys.filter(
    (journey) => journey.status === CONCLUDED_STATUS,
  )

  const progress = !isEmpty(trails)
    ? (size(concludedJourneys) / size(trails)) * 100
    : 0

  const notConcludedJourneys = trainningJourneys.find(
    (journey) => journey.status !== CONCLUDED_STATUS,
  )

  const selectedJourneyId = notConcludedJourneys
    ? notConcludedJourneys.id
    : trainningJourneys[0]?.id

  const defineStatus = () => {
    if (size(concludedJourneys) === size(trails) && !isEmpty(trails))
      return CONCLUDED_STATUS
    if (size(concludedJourneys) > 0) return DOING_STATUS
    return NOT_INITIATED_STATUS
  }

  const status = defineStatus()

  const onRedirect = () => {
    helpers.mixpanel.track('Home', 'Treinamento', {
      action: 'treinamento-home',
    })

    if (status === CONCLUDED_STATUS) return setOpenDialog(true)

    history.push(
      reverse(routes.trainningJourney, {
        trainningJourneyId: selectedJourneyId,
      }),
    )
  }

  const handleResetJourney = (journeyId) => {
    history.push(
      `${reverse(routes.trainningJourney, {
        trainningJourneyId: journeyId,
      })}?step=0`,
    )
  }

  const handleTrailAction = async ({
    journeyId,
    isConcluded,
    certificationKid,
  }) => {
    if (isConcluded) {
      try {
        setLoading(true)

        const userCertification = user?.userCompanyCertifications?.find(
          (cert) => cert.certType === certificationKid,
        )

        const certificateResponse = await service.dponet.companyTrails.getImage(
          userCertification?.certification,
        )

        imageToPdf(
          certificateResponse?.data,
          changeCase.methods.snakeCase(
            `Certificado ${TRANSLATE_TYPES[certificationKid]}`,
          ),
        )
      } catch (error) {
        console.error(error)
        snackbar.open({
          message:
            formatters.errorMessage(error.response.data.error) ||
            'Ocorreu um erro ao baixar o certificado.',
          variant: 'error',
        })
      } finally {
        setLoading(false)
      }
      return
    }

    history.push(
      reverse(routes.trainningJourney, {
        trainningJourneyId: journeyId,
      }),
    )
  }

  return (
    <Fragment>
      <TrailsCard
        title={`${step} - Treinamentos de Conscientização`}
        disabled={disabled && progress < 100}
        loading={isLoading || loading}
        journey={JOURNEYS.TRAINNING_JOURNEY}
        trails={trails}
        progress={progress}
        status={status}
        onClick={onRedirect}
      />
      <BaseDialog
        open={openDialog}
        setOpen={setOpenDialog}
        title="Selecione o treinamento que deseja refazer"
        closeButtonText="Cancelar"
      >
        <Box
          py={2}
          display="flex"
          flexDirection="column"
          gridGap={theme.spacing(2)}
        >
          <Paper className={classes.paper}>
            <Table>
              <TableBody>
                {concludedJourneys.map((journey) => (
                  <TableRow key={journey.id}>
                    <TableCell width="90%">
                      {TRANSLATE_TYPES[journey.certificationKind]}
                    </TableCell>
                    <TableCell width="10%" className={classes.lineAction}>
                      <IconButton
                        onClick={() => handleResetJourney(journey.id)}
                      >
                        <RotateCcwIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </BaseDialog>
    </Fragment>
  )
}

TrainningJourneyTrail.propTypes = {
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
  user: PropTypes.object,
}

export default TrainningJourneyTrail
